// The "thank you" moment after a real-money Gem purchase: a pop-up shows what arrived, and on Collect
// the Gems fly into the gem pill while its number counts up. The Gems are already credited and saved
// (src/platform/payments.ts); this only holds the HUD number back until the player collects.
import { GEM_PACKS } from '../data/gemPacks';

export interface GemHud {
  /** Show this number in the gem pill instead of the real balance (null = real balance). */
  holdGems(n: number | null): void;
}

const queue: number[] = [];
let showing = false;

/** Queues a reward pop-up for `gems` just credited on top of `balance` (the new total). */
export function celebrateGems(gems: number, balance: () => number, hud: GemHud): void {
  queue.push(gems);
  if (!showing) next(balance, hud);
}

function next(balance: () => number, hud: GemHud): void {
  const gems = queue.shift();
  if (gems === undefined) { showing = false; hud.holdGems(null); return; }
  showing = true;
  // Everything still queued is credited too: hold the pill below all of it.
  const pending = queue.reduce((a, b) => a + b, 0);
  const from = balance() - gems - pending;
  hud.holdGems(from);

  const el = document.createElement('div');
  el.id = 'gem-reward';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.innerHTML = `<div class="gr-card">
    <div class="gr-rays"></div>
    <p class="gr-kicker">Thank you for your purchase!</p>
    <div class="gr-art"><img src="art/shop/shop_gems_${String(tierFor(gems)).padStart(2, '0')}.webp" alt=""></div>
    <b class="gr-amt">+${gems.toLocaleString()}</b><span class="gr-unit">Gems</span>
    <button class="gr-collect">Collect</button></div>`;
  document.body.append(el);
  const btn = el.querySelector<HTMLButtonElement>('.gr-collect')!;
  btn.focus();
  btn.addEventListener('click', () => {
    btn.disabled = true;
    collect(el, from, gems, hud).then(() => { el.remove(); next(balance, hud); });
  }, { once: true });
}

/** Gems fly from the pop-up to the pill; the pill counts up as they land. */
async function collect(el: HTMLElement, from: number, gems: number, hud: GemHud): Promise<void> {
  const pill = document.querySelector('.pill.gems .pi')!.getBoundingClientRect();
  const art = el.querySelector('.gr-art')!.getBoundingClientRect();
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.classList.add('out');
  const N = reduce ? 0 : 14, flights: Promise<unknown>[] = [];
  for (let i = 0; i < N; i++) {
    const g = document.createElement('i');
    g.className = 'ic ic-gem gr-fly';
    const x0 = art.left + art.width / 2 + (Math.random() - 0.5) * art.width * 0.7;
    const y0 = art.top + art.height / 2 + (Math.random() - 0.5) * art.height * 0.5;
    const x1 = pill.left + pill.width / 2, y1 = pill.top + pill.height / 2;
    g.style.left = `${x0}px`; g.style.top = `${y0}px`;
    document.body.append(g);
    const anim = g.animate([
      { transform: 'translate(-50%,-50%) scale(1)', offset: 0 },
      { transform: `translate(calc(-50% + ${(x1 - x0) * 0.3}px), calc(-50% + ${(y1 - y0) * 0.1 - 60}px)) scale(1.25)`, offset: 0.35 },
      { transform: `translate(calc(-50% + ${x1 - x0}px), calc(-50% + ${y1 - y0}px)) scale(.55)`, offset: 1 },
    ], { duration: 700, delay: i * 45, easing: 'cubic-bezier(.5,0,.75,.4)', fill: 'forwards' });
    flights.push(anim.finished.then(() => {
      g.remove();
      // Each landing gem moves the pill a step closer to the new total.
      hud.holdGems(Math.round(from + (gems * (i + 1)) / N));
      const p = document.querySelector('.pill.gems');
      p?.classList.remove('bump'); void (p as HTMLElement | null)?.offsetWidth; p?.classList.add('bump');
    }));
  }
  await Promise.all(flights);
  hud.holdGems(from + gems);
  await new Promise((r) => setTimeout(r, 250));
}

/** The shop's painted Gem pack picture (1-10) that best matches an amount (custom amounts included). */
function tierFor(gems: number): number {
  let tier = 1;
  GEM_PACKS.forEach((p, i) => { if (gems >= p.gems) tier = i + 1; });
  return tier;
}
